import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
// eslint-disable-next-line no-restricted-imports
import type { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import type { Location } from 'react-router-dom';
import dayjs from '~/timeInit';

import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
  GridPaginationModel,
  GridRowModel,
  getGridNumericOperators,
  getGridStringOperators,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { DatePicker } from '@mui/x-date-pickers-pro';

import StyledDataGrid from '~/components/CustomerCommunication/HeroPlanner/Components/ScheduleForms/StyledDataGrid';

import { charterInventoryManagementQueryKeys } from '~/hooks/apis/flights-charter/charterInventoryManagementQueryKeys';
import {
  useGetCharterInventoryManagement,
  useUpdateCharterInventoryManagement,
} from '~/hooks/apis/flights-charter/useCharterInventoryManagement';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { CharterInventoryManagementParams, CharterInventoryManagementRow } from '~/services/CharterService';
import { fetchCharterInventoryManagement } from '~/services/CharterService';
import { formatDateISO } from '~/services/TimeService';

import { isAdmin } from '~/utils/adminPermission';
import { downloadCsvFile } from '~/utils/csvDownload';

import {
  INVENTORY_MANAGEMENT_CSV_HEADERS,
  INVENTORY_MANAGEMENT_DEPARTING_ARR_AIRPORT,
  INVENTORY_MANAGEMENT_DEPARTING_DEP_AIRPORT,
  INVENTORY_MANAGEMENT_RETURNING_ARR_AIRPORT,
  INVENTORY_MANAGEMENT_RETURNING_DEP_AIRPORT,
  buildInventoryManagementUpdatePayload,
  cabinClassLabel,
  formatInventoryDate,
  formatInventoryRoute,
  formatInventoryRouteAscii,
  formatUnallocatedSeatCount,
  getInventoryManagementCsvRows,
  parseAndValidateInventoryRow,
  seatingClassLabel,
  sortInventoryManagementRows,
  validateInventoryManagementRows,
  withComputedInventoryUsage,
} from './CharterInventoryManagementHelpers';
import InventoryDateFilterInput, { InventoryDateFilterMultipleInput } from './InventoryDateFilterInput';
import { serializeInventoryFilters } from './charterInventoryFilters';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const CSV_EXPORT_PAGE_SIZE = 1000;
const MAX_AUTO_HEIGHT_ROWS = 5;

type SearchFilters = Pick<
  CharterInventoryManagementParams,
  'departureDateTo' | 'direction' | 'inventoryFilters' | 'inventoryFilterLogic'
> & { departureDateFrom: string };

export default function CharterInventoryManagementPage(): React.JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { block, push } = useAppNavigation();
  const { user } = useCurrentUser();
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [departureDateFrom, setDepartureDateFrom] = useState<Dayjs | null>(() => dayjs());
  const [departureDateTo, setDepartureDateTo] = useState<Dayjs | null>(null);
  const [direction, setDirection] = useState<NonNullable<SearchFilters['direction']> | ''>('');
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [], logicOperator: GridLogicOperator.And });
  const [searchFilters, setSearchFilters] = useState<SearchFilters>(() => ({
    departureDateFrom: dayjs().format('YYYY-MM-DD'),
  }));
  const [pendingSearch, setPendingSearch] = useState<SearchFilters | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const { paginationModel, setPaginationModel } = useDataGridPagination({ page: 0, pageSize: 50 });
  const [isViewMode, setIsViewMode] = useState(true);
  const [editedRows, setEditedRows] = useState<Map<string, CharterInventoryManagementRow>>(new Map());
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<Location | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);

  const hasUnsavedChanges = editedRows.size > 0;
  const canEditInventory = isAdmin(user);
  const offset = paginationModel.page * paginationModel.pageSize;
  const limit = paginationModel.pageSize;

  const queryParams = useMemo(
    () => ({
      departingDepAirport: INVENTORY_MANAGEMENT_DEPARTING_DEP_AIRPORT,
      departingArrAirport: INVENTORY_MANAGEMENT_DEPARTING_ARR_AIRPORT,
      returningDepAirport: INVENTORY_MANAGEMENT_RETURNING_DEP_AIRPORT,
      returningArrAirport: INVENTORY_MANAGEMENT_RETURNING_ARR_AIRPORT,
      ...searchFilters,
      offset,
      limit,
    }),
    [limit, offset, searchFilters],
  );

  const inventoryQuery = useGetCharterInventoryManagement(queryParams);
  const updateMutation = useUpdateCharterInventoryManagement();

  useEffect(() => {
    if (!canEditInventory && !isViewMode) {
      setEditedRows(new Map());
      setCellModesModel({});
      setIsViewMode(true);
    }
  }, [canEditInventory, isViewMode]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      unblockRef.current = block((location) => {
        setPendingNavigation(location);
        setIsDiscardDialogOpen(true);
        return false;
      });
    } else if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [block, hasUnsavedChanges]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const baseRows = useMemo(() => inventoryQuery.data?.result ?? [], [inventoryQuery.data?.result]);

  const baseRowMap = useMemo(() => {
    const map = new Map<string, CharterInventoryManagementRow>();
    for (const row of baseRows) {
      map.set(row.id, row);
    }
    return map;
  }, [baseRows]);

  const rows = useMemo(() => {
    return sortInventoryManagementRows(
      withComputedInventoryUsage(baseRows.map((baseRow) => editedRows.get(baseRow.id) ?? baseRow)),
    );
  }, [baseRows, editedRows]);

  // Keep all allocations for capacity checks; hide only the rows that did not match the search.
  const visibleRows = useMemo(() => rows.filter((row) => row.matchesFilters !== false), [rows]);
  const validationErrors = useMemo(() => validateInventoryManagementRows(rows), [rows]);
  const invalidDateFrom = !departureDateFrom?.isValid();
  const invalidDateTo = departureDateTo !== null && !departureDateTo.isValid();
  const invalidDateRange = !invalidDateFrom && !invalidDateTo && departureDateTo?.isBefore(departureDateFrom, 'day');

  const applySearch = useCallback(
    (filters: SearchFilters) => {
      setSearchFilters(filters);
      setPaginationModel((current) => ({ ...current, page: 0 }));
      setCellModesModel({});
      setIsViewMode(true);
      apiRef.current.hideFilterPanel();
      // Pressing Search again also refreshes an unchanged applied search.
      void queryClient.invalidateQueries({
        queryKey: charterInventoryManagementQueryKeys.list({ ...queryParams, ...filters, offset: 0 }),
        exact: true,
      });
    },
    [apiRef, queryClient, queryParams, setPaginationModel],
  );

  const processRowUpdate = useCallback(
    (newRow: GridRowModel<CharterInventoryManagementRow>) => {
      const validatedRow = parseAndValidateInventoryRow(newRow);
      const original = baseRowMap.get(validatedRow.id);

      setEditedRows((current) => {
        const next = new Map(current);
        if (original && validatedRow.allocation === original.allocation) {
          next.delete(validatedRow.id);
        } else {
          next.set(validatedRow.id, validatedRow);
        }
        return next;
      });

      return validatedRow;
    },
    [baseRowMap],
  );

  const handleProcessRowUpdateError = useCallback(
    (error: Error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  const handlePaginationModelChange = useCallback(
    (nextPaginationModel: GridPaginationModel) => {
      if (hasUnsavedChanges) {
        enqueueSnackbar('Save or discard inventory changes before changing pages.', {
          variant: 'warning',
        });
        return;
      }

      setPaginationModel(nextPaginationModel);
    },
    [enqueueSnackbar, hasUnsavedChanges, setPaginationModel],
  );

  const handleCellClick = useCallback(
    (params: GridCellParams, event: React.MouseEvent) => {
      if (isViewMode || !params.isEditable) {
        return;
      }

      if ((event.target as HTMLElement).nodeType === 1 && !event.currentTarget.contains(event.target as Element)) {
        return;
      }

      setCellModesModel((prevModel) => ({
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {},
            ),
          }),
          {},
        ),
        [params.id]: {
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {},
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      }));
    },
    [isViewMode],
  );

  const getCellClassName = useCallback(
    (params: GridCellParams<CharterInventoryManagementRow>) => {
      if (params.field !== 'allocation') {
        return '';
      }
      const original = baseRowMap.get(params.row.id);
      return original && params.row.allocation !== original.allocation ? 'cell-modified' : '';
    },
    [baseRowMap],
  );

  const handleConfirmDiscard = useCallback(() => {
    setEditedRows(new Map());
    setCellModesModel({});
    setIsDiscardDialogOpen(false);

    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    if (pendingSearch) {
      applySearch(pendingSearch);
      setPendingSearch(null);
    } else if (pendingNavigation) {
      const targetPath = pendingNavigation.pathname + pendingNavigation.search + pendingNavigation.hash;
      setPendingNavigation(null);
      push(targetPath);
    } else {
      setIsViewMode(true);
    }
  }, [applySearch, pendingNavigation, pendingSearch, push]);

  const handleCancelDiscard = useCallback(() => {
    setIsDiscardDialogOpen(false);
    setPendingNavigation(null);
    setPendingSearch(null);
  }, []);

  const handleCancelEditing = useCallback(() => {
    // The cell's blur commit may still be pending when Cancel is clicked.
    const hasPendingCellChanges = baseRows.some((row) => {
      const updatedRow = apiRef.current.getRowWithUpdatedValues(row.id, 'allocation');
      return updatedRow && Number(updatedRow.allocation) !== row.allocation;
    });

    if (hasUnsavedChanges || hasPendingCellChanges) {
      setIsDiscardDialogOpen(true);
    } else {
      setCellModesModel({});
      setIsViewMode(true);
    }
  }, [apiRef, baseRows, hasUnsavedChanges]);

  const handleSave = useCallback(async () => {
    if (!canEditInventory) {
      enqueueSnackbar('Only admin users can edit inventory allocations.', { variant: 'error' });
      return;
    }

    if (updateMutation.isPending) {
      return;
    }

    if (editedRows.size === 0) {
      enqueueSnackbar('No changes to save.', { variant: 'warning' });
      return;
    }

    if (validationErrors.length > 0) {
      enqueueSnackbar(validationErrors[0], { variant: 'error' });
      return;
    }

    try {
      await updateMutation.mutateAsync(buildInventoryManagementUpdatePayload(editedRows));
      enqueueSnackbar(`Inventory allocations saved (${editedRows.size} rows updated).`, { variant: 'success' });
      setEditedRows(new Map());
      setCellModesModel({});
      setIsViewMode(true);
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to save inventory allocations.', {
        variant: 'error',
      });
    }
  }, [canEditInventory, editedRows, enqueueSnackbar, updateMutation, validationErrors]);

  const handleDownloadCsv = useCallback(async () => {
    if (isExportingCsv) {
      return;
    }

    if (hasUnsavedChanges) {
      enqueueSnackbar('Save or discard inventory changes before exporting CSV.', {
        variant: 'warning',
      });
      return;
    }

    setIsExportingCsv(true);

    try {
      const allRows: Array<CharterInventoryManagementRow> = [];
      let offset = 0;
      let totalItems: number | null = null;

      do {
        const response = await fetchCharterInventoryManagement({
          ...queryParams,
          offset,
          limit: CSV_EXPORT_PAGE_SIZE,
        });

        allRows.push(...response.result);
        totalItems = response.pagination.total_items;
        offset += response.pagination.limit;

        if (response.result.length === 0) {
          break;
        }
      } while (totalItems === null || offset < totalItems);

      if (allRows.length === 0) {
        enqueueSnackbar('No inventory rows available to export.', { variant: 'warning' });
        return;
      }

      const exportRows = sortInventoryManagementRows(withComputedInventoryUsage(allRows)).filter(
        (row) => row.matchesFilters !== false,
      );
      downloadCsvFile(
        getInventoryManagementCsvRows(exportRows),
        INVENTORY_MANAGEMENT_CSV_HEADERS,
        `charter-inventory-management-${formatDateISO()}.csv`,
      );
      enqueueSnackbar(`CSV exported successfully with ${exportRows.length} rows.`, {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to export inventory CSV.', {
        variant: 'error',
      });
    } finally {
      setIsExportingCsv(false);
    }
  }, [enqueueSnackbar, hasUnsavedChanges, isExportingCsv, queryParams]);

  const columns = useMemo<Array<GridColDef<CharterInventoryManagementRow>>>(() => {
    const definitions: Array<GridColDef<CharterInventoryManagementRow>> = [
      {
        field: 'depDate',
        filterOperators: getGridStringOperators().map((operator) => ({
          ...operator,
          ...(operator.InputComponent
            ? {
                InputComponent:
                  operator.value === 'isAnyOf' ? InventoryDateFilterMultipleInput : InventoryDateFilterInput,
              }
            : {}),
        })),
        rowSpanValueGetter: (_value, row) => row.flightInstanceId,
        headerName: 'Departure Date',
        flex: 1,
        minWidth: 150,
        valueFormatter: (value) => formatInventoryDate(String(value)),
      },
      {
        field: 'direction',
        rowSpanValueGetter: (_value, row) => row.flightInstanceId,
        headerName: 'Direction',
        flex: 0.8,
        minWidth: 120,
        valueGetter: (_value, row) => formatInventoryRouteAscii(row),
        renderCell: (params) => formatInventoryRoute(params.row),
      },
      {
        field: 'cabinClass',
        rowSpanValueGetter: (_value, row) => row.id,
        headerName: 'Cabin class',
        flex: 1.4,
        minWidth: 190,
        valueGetter: (_value, row) => cabinClassLabel[row.cabinClass],
      },
      {
        field: 'allocation',
        headerName: 'Allocation',
        flex: 0.8,
        minWidth: 125,
        type: 'number',
        editable: true,
        rowSpanValueGetter: (_value, row) => row.id,
      },
      {
        field: 'booked',
        rowSpanValueGetter: (_value, row) => row.id,
        headerName: 'Booked',
        flex: 0.7,
        minWidth: 105,
        type: 'number',
      },
      {
        field: 'availableSeats',
        rowSpanValueGetter: (_value, row) => row.id,
        headerName: 'Available',
        flex: 0.8,
        minWidth: 115,
        type: 'number',
      },
      {
        field: 'seatingClass',
        headerName: 'Seating Class',
        flex: 1.2,
        minWidth: 190,
        valueGetter: (_value, row) => seatingClassLabel[row.seatingClass],
        rowSpanValueGetter: (_value, row) => `${row.flightInstanceId}-${row.seatingClass}`,
      },
      {
        field: 'seatingClassCapacity',
        filterOperators: [
          ...getGridStringOperators(),
          ...getGridNumericOperators().filter(
            (operator) => !getGridStringOperators().some((textOperator) => textOperator.value === operator.value),
          ),
        ],
        headerName: 'Class Capacity',
        renderHeader: () => (
          <Box sx={{ lineHeight: 1.2 }}>
            <Typography variant="body2" fontWeight={500}>
              Class Capacity
            </Typography>
            <Typography variant="caption" color="text.secondary">
              (allocated/total)
            </Typography>
          </Box>
        ),
        flex: 1.2,
        minWidth: 240,
        renderCell: (params) => (
          <Box sx={{ lineHeight: 1.25, overflow: 'hidden' }}>
            <Typography variant="body2">
              {params.row.seatingClassUsage}/{params.row.seatingClassCapacity}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatUnallocatedSeatCount(params.row)}
            </Typography>
          </Box>
        ),
        rowSpanValueGetter: (_value, row) => `${row.flightInstanceId}-${row.seatingClass}`,
      },
    ];
    return definitions.map((column) => ({
      ...column,
      filterOperators:
        column.filterOperators ?? (column.type === 'number' ? getGridNumericOperators() : getGridStringOperators()),
    }));
  }, []);

  const handleSearch = () => {
    if (invalidDateFrom || invalidDateTo || invalidDateRange || updateMutation.isPending || isExportingCsv) return;
    try {
      const filters: SearchFilters = {
        departureDateFrom: departureDateFrom.format('YYYY-MM-DD'),
        departureDateTo: departureDateTo?.format('YYYY-MM-DD'),
        direction: direction || undefined,
        inventoryFilters: serializeInventoryFilters(filterModel, columns),
        inventoryFilterLogic: filterModel.logicOperator === GridLogicOperator.Or ? 'or' : 'and',
      };
      setSearchError(null);
      const hasPendingCellChanges = baseRows.some((row) => {
        const updatedRow = apiRef.current.getRowWithUpdatedValues(row.id, 'allocation');
        return updatedRow && Number(updatedRow.allocation) !== row.allocation;
      });
      if (hasUnsavedChanges || hasPendingCellChanges) {
        setPendingSearch(filters);
        setIsDiscardDialogOpen(true);
      } else {
        applySearch(filters);
      }
    } catch (error) {
      setSearchError(error instanceof Error ? error.message : 'Check the column filters.');
    }
  };

  const appliedFilterSummary = [
    `From ${formatInventoryDate(searchFilters.departureDateFrom)}`,
    searchFilters.departureDateTo ? `to ${formatInventoryDate(searchFilters.departureDateTo)}` : 'No end date',
    searchFilters.direction === 'departing'
      ? 'MEL → MLE'
      : searchFilters.direction === 'returning'
        ? 'MLE → MEL'
        : 'Both directions',
    searchFilters.inventoryFilters
      ? JSON.parse(searchFilters.inventoryFilters)
          .map((filter) => {
            const label = columns.find((column) => column.field === filter.field)?.headerName;
            const operator = filter.operator.replace(/([A-Z])/g, ' $1').toLowerCase();
            const formatValue = (value: string | number) =>
              filter.field === 'depDate' ? formatInventoryDate(String(value)) : value;
            const value = Array.isArray(filter.value)
              ? filter.value.map(formatValue).join(', ')
              : filter.value === undefined
                ? ''
                : formatValue(filter.value);
            return `${label} ${operator} ${value}`.trim();
          })
          .join(searchFilters.inventoryFilterLogic === 'or' ? ' OR ' : ' AND ')
      : 'All allocations',
  ].join(' · ');

  const draftFiltersChanged = useMemo(() => {
    try {
      return (
        departureDateFrom?.format('YYYY-MM-DD') !== searchFilters.departureDateFrom ||
        departureDateTo?.format('YYYY-MM-DD') !== searchFilters.departureDateTo ||
        (direction || undefined) !== searchFilters.direction ||
        serializeInventoryFilters(filterModel, columns) !== searchFilters.inventoryFilters ||
        (filterModel.items.length > 1 &&
          (filterModel.logicOperator === GridLogicOperator.Or ? 'or' : 'and') !== searchFilters.inventoryFilterLogic)
      );
    } catch {
      // An unfinished filter is a draft, not an applied search.
      return true;
    }
  }, [columns, departureDateFrom, departureDateTo, direction, filterModel, searchFilters]);

  const hasData = !inventoryQuery.isLoading && !inventoryQuery.isError;

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4" fontWeight={600}>
          Inventory management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Find departures and manage their cabin allocations.
        </Typography>
      </Stack>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Search departures
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <DatePicker
              label="Departure date from"
              value={departureDateFrom}
              onChange={setDepartureDateFrom}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  required: true,
                  error: invalidDateFrom,
                  helperText: invalidDateFrom ? 'Enter a valid departure date.' : undefined,
                },
              }}
            />
            <DatePicker
              label="Departure date to"
              value={departureDateTo}
              onChange={setDepartureDateTo}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  error: invalidDateTo || Boolean(invalidDateRange),
                  helperText: invalidDateTo
                    ? 'Enter a valid departure date.'
                    : invalidDateRange
                      ? 'End date must be on or after the start date.'
                      : undefined,
                  placeholder: 'No end date',
                },
              }}
            />
            <TextField
              select
              label="Direction"
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              value={direction}
              onChange={(event) => setDirection(event.target.value as typeof direction)}
              fullWidth
              size="small"
            >
              <MenuItem value="">Both directions</MenuItem>
              <MenuItem value="departing">MEL → MLE</MenuItem>
              <MenuItem value="returning">MLE → MEL</MenuItem>
            </TextField>
          </Stack>
          {searchError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {searchError}
            </Alert>
          )}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            gap={2}
            sx={{ mt: 2 }}
          >
            <Button
              ref={filterButtonRef}
              startIcon={<FilterListIcon />}
              onClick={() => apiRef.current.showFilterPanel()}
              disabled={!hasData}
            >
              Column filters ({filterModel.items.length})
            </Button>
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button
                onClick={() => {
                  setDepartureDateFrom(dayjs());
                  setDepartureDateTo(null);
                  setDirection('');
                  setFilterModel({ items: [], logicOperator: GridLogicOperator.And });
                  setSearchError(null);
                }}
                disabled={updateMutation.isPending || isExportingCsv}
              >
                Reset filters
              </Button>
              <LoadingButton
                variant="contained"
                onClick={handleSearch}
                loading={inventoryQuery.isFetching}
                disabled={
                  invalidDateFrom ||
                  invalidDateTo ||
                  Boolean(invalidDateRange) ||
                  updateMutation.isPending ||
                  isExportingCsv
                }
              >
                Search
              </LoadingButton>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {inventoryQuery.isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading inventory...
          </Typography>
        </Box>
      ) : null}

      {inventoryQuery.isError ? (
        <Box mt={4}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" variant="outlined" onClick={() => inventoryQuery.refetch()}>
                Retry
              </Button>
            }
          >
            <AlertTitle>Failed to load inventory</AlertTitle>
            {inventoryQuery.error instanceof Error ? inventoryQuery.error.message : 'An unexpected error occurred'}
          </Alert>
        </Box>
      ) : null}

      {hasData ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                gap={2}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1">Results</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {appliedFilterSummary}
                  </Typography>
                </Box>
                <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ flexShrink: 0 }}>
                  <LoadingButton
                    variant="text"
                    onClick={handleDownloadCsv}
                    loading={isExportingCsv}
                    disabled={isExportingCsv}
                    startIcon={<DownloadIcon />}
                  >
                    Export matching CSV
                  </LoadingButton>
                  {canEditInventory && isViewMode && (
                    <Button
                      variant="contained"
                      onClick={() => setIsViewMode(false)}
                      disabled={!visibleRows.length || inventoryQuery.isFetching}
                    >
                      Edit inventory
                    </Button>
                  )}
                </Stack>
              </Stack>
              {draftFiltersChanged && <Alert severity="info">Filters changed. Search to update results.</Alert>}
              {validationErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {validationErrors[0]}
                </Alert>
              )}

              <Box
                sx={{
                  width: '100%',
                  height: visibleRows.length <= MAX_AUTO_HEIGHT_ROWS ? 'auto' : 'clamp(320px, 55vh, 620px)',
                  display: 'flex',
                  flexDirection: 'column',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ flex: visibleRows.length <= MAX_AUTO_HEIGHT_ROWS ? 'initial' : 1, minHeight: 0 }}>
                  <StyledDataGrid
                    apiRef={apiRef}
                    rows={visibleRows}
                    columns={columns}
                    autoHeight={visibleRows.length <= MAX_AUTO_HEIGHT_ROWS}
                    localeText={{
                      noRowsLabel: 'No matching inventory allocations found.',
                      MuiTablePagination: { labelRowsPerPage: 'Departures per page:' },
                    }}
                    unstable_rowSpanning
                    pagination
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    rowCount={inventoryQuery.data?.pagination.total_items ?? 0}
                    editMode="cell"
                    isCellEditable={(params) => canEditInventory && !isViewMode && params.field === 'allocation'}
                    cellModesModel={cellModesModel}
                    onCellModesModelChange={setCellModesModel}
                    onCellClick={handleCellClick}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    getCellClassName={getCellClassName}
                    filterMode="server"
                    filterDebounceMs={0}
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    slotProps={{
                      // Cached results can mount the grid before the button ref is attached.
                      panel: { anchorEl: () => filterButtonRef.current },
                      filterPanel: { disableAddFilterButton: filterModel.items.length >= 20 },
                    }}
                    sx={{
                      '& .cell-modified': {
                        backgroundColor: 'warning.light',
                      },
                      ...(!isViewMode && {
                        '& .MuiDataGrid-cell[data-field="allocation"]': {
                          outline: '1px solid',
                          outlineColor: 'grey.400',
                          outlineOffset: -1,
                          borderRadius: 1,
                          backgroundColor: 'background.paper',
                          '&:hover': {
                            outlineColor: 'primary.main',
                          },
                        },
                        '& .MuiDataGrid-cell--editing': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: -2,
                          boxShadow: 1,
                          backgroundColor: 'background.paper',
                        },
                      }),
                    }}
                    disableRowSelectionOnClick
                  />
                </Box>
                {canEditInventory && !isViewMode && (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    gap={2}
                    sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
                  >
                    <Typography variant="body2" color="text.secondary" aria-live="polite">
                      {hasUnsavedChanges
                        ? `${editedRows.size} ${editedRows.size === 1 ? 'allocation' : 'allocations'} with unsaved changes`
                        : 'Edit an allocation in the table to make changes.'}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Button variant="text" disabled={updateMutation.isPending} onClick={handleCancelEditing}>
                        Cancel editing
                      </Button>
                      <LoadingButton
                        variant="contained"
                        onClick={handleSave}
                        loading={updateMutation.isPending}
                        disabled={!hasUnsavedChanges || updateMutation.isPending || validationErrors.length > 0}
                      >
                        Save changes
                      </LoadingButton>
                    </Stack>
                  </Stack>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={isDiscardDialogOpen} onClose={handleCancelDiscard}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved inventory changes.{' '}
            {pendingNavigation
              ? 'If you leave this page, they will be discarded.'
              : pendingSearch
                ? 'If you search, they will be discarded.'
                : 'If you leave Edit Mode, they will be discarded.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDiscard} variant="text">
            Discard changes
          </Button>
          <Button onClick={handleCancelDiscard} variant="contained" color="primary">
            Keep editing
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
